import { styled, useTheme } from '@mui/material/styles'
import { Button, ButtonProps, IconButton, IconButtonProps } from '@mui/material'
import { FC, useMemo } from 'react'
import { merge } from 'lodash'

export const KButton: FC<
  Omit<ButtonProps, 'variant' | 'color'> & {
    variant?: 'primary' | 'secondary' | 'danger'
  }
> = (props) => {
  const theme = useTheme()
  const { variant, sx, ...restProps } = props

  const variantSx = useMemo(() => {
    switch (variant) {
      case 'secondary':
        return {
          border: `1px solid ${theme.palette.grey['400']}`,
          backgroundColor: theme.palette.common.white,
          color: theme.palette.secondary.main,
          '&.Mui-disabled': {
            border: `1px solid ${theme.palette.grey['200']}`,
            backgroundColor: theme.palette.grey.A100,
          },
          '&:hover': {
            border: 'none',
            color: theme.palette.common.white,
            backgroundColor: theme.palette.secondary.main,
            boxShadow: 'none',
          },
          '&:active': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.dark,
          },
        }
      case 'danger':
        return {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.common.white,
          '&.Mui-disabled': {
            backgroundColor: theme.palette.error.light,
          },
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
          },
          '&:active': {
            backgroundColor: theme.palette.error.dark,
          },
        }
      default:
        return {
          '&.Mui-disabled': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.light,
          },
          '&:hover': {
            backgroundColor: theme.palette.primary['200'],
          },
          '&:active': {
            backgroundColor: theme.palette.primary.dark,
          },
        }
    }
  }, [theme, variant])

  const sizeSx = useMemo(() => {
    return props.size === 'small'
      ? {
          height: 44,
          ...theme.typography.subtitle1,
          fontWeight: 700,
          [theme.breakpoints.down('sm')]: {
            height: 40,
            fontSize: theme.typography.subtitle2.fontSize,
          },
        }
      : props.size === 'medium'
        ? {
            height: 56,
            ...theme.typography.h5,
            [theme.breakpoints.down('sm')]: {
              height: 48,
              fontSize: theme.typography.subtitle1.fontSize,
            },
          }
        : {
            height: 66,
            ...theme.typography.h4,
            [theme.breakpoints.down('sm')]: {
              height: 56,
              fontSize: theme.typography.h5.fontSize,
            },
          }
  }, [theme, props.size])

  return (
    <Button
      {...restProps}
      variant="contained"
      sx={merge(
        {
          borderRadius: 50, // Any big number will do
          boxShadow: 'none',
          ...sizeSx,
          ...variantSx,
        },
        sx,
      )}
    />
  )
}

export const KToggleButton = styled(KButton)(({ theme, variant }) => ({
  borderRadius: 8,
  ...(variant === 'secondary' && {
    border: `1px solid ${theme.palette.grey['400']}`,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.secondary.main,
    '&.Mui-disabled': {
      border: `1px solid ${theme.palette.grey['200']}`,
      backgroundColor: theme.palette.grey.A100,
    },
    '&:hover': {
      border: `1px solid ${theme.palette.secondary.main}`,
      color: theme.palette.common.white,
      backgroundColor: theme.palette.secondary.main,
      boxShadow: 'none',
    },
    '&:active': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(variant === 'primary' && {
    '&.Mui-disabled': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.light,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary['200'],
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
}))

export const KIconButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  border: `1px solid ${theme.palette.grey[400]}`,
  backgroundColor: 'transparent',
}))

export const KSmallIconButton: FC<
  Omit<IconButtonProps, 'variant'> & {
    variant?: 'primary' | 'secondary'
  }
> = (props) => {
  const { variant } = props
  const theme = useTheme()

  return (
    <IconButton
      {...props}
      sx={{
        width: 24,
        height: 24,
        padding: 0,
        color:
          variant === 'secondary'
            ? theme.palette.secondary.main
            : theme.palette.primary.main,
        backgroundColor: 'transparent',
      }}
    />
  )
}
